import React from 'react';
import { ChevronDown, ArrowRight, Mail, Shield, Activity, Brain, MapPin, Instagram, Twitter, Linkedin, Globe, Menu, X, Facebook, Check } from 'lucide-react';

const BounceDataPlatformPage = () => {
    const [email, setEmail] = React.useState('');
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [submitStatus, setSubmitStatus] = React.useState('');
    const [showSuccessModal, setShowSuccessModal] = React.useState(false);

    const handleChange = (e:any) => {
        setEmail(e.target.value);
    }

    const handleSubmit = async (e:any) => {
        e.preventDefault();
        if (!email) return;

        setIsSubmitting(true);
        setSubmitStatus('');

        try {
            const response = await fetch('https://api.bunce.so/v1/segments/9f138305-6b1f-4e68-93cc-0636be7a64c0/customers', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Authorization': 'sk_live_ba940390a68f28a387a8c0f49a2b258ef67750af'
                },
                body: JSON.stringify({
                    customers: [
                        {
                            email: email
                        }
                    ]
                })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSubmitStatus('success');
                setEmail('');
                setShowSuccessModal(true);
            } else {
                setSubmitStatus('error');
            }
        } catch (error) {
            console.error('Error adding to waitlist:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const closeSuccessModal = () => {
        setShowSuccessModal(false);
        setSubmitStatus('');
    };

    return (
        <div className="min-h-screen bg-white">
            {/* Success Modal */}
            {showSuccessModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 relative animate-in fade-in-0 zoom-in-95 duration-300">
                        <button
                            onClick={closeSuccessModal}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        
                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check className="w-8 h-8 text-green-600" />
                            </div>
                            
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                You're on the waitlist!
                            </h3>
                            
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                Thank you for joining our waitlist. We'll notify you as soon as our AI-powered fraud detection platform is ready for you.
                            </p>
                            
                            <button
                                onClick={closeSuccessModal}
                                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                            >
                                Got it, thanks!
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <header className="sticky top-0 z-40 right-0 left-0 bg-gradient-to-br from-gray-50 to-blue-50 p-4 lg:p-5">
                <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 bg-white rounded-lg">
                    <div className="flex justify-between items-center py-3 lg:py-4">
                        <div className="flex items-center space-x-2">
                            <a href="/">
                                <img src='full_logo.svg' className="h-8 w-auto" alt="Bunce Logo" />
                            </a>
                        </div>

                        {/* Desktop Navigation */}
                        <nav className="hidden md:flex items-center space-x-8">
                            <a href="#products" className="text-gray-700 hover:text-gray-900">Products</a>
                            <a href="#products" className="text-gray-700 hover:text-gray-900">About us</a>
                            <a href="#contact-us" className="text-gray-700 hover:text-gray-900">Contact</a>
                        </nav>

                        {/* Desktop CTA Button */}
                        <a href='#contact-us' className="hidden md:block bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium">
                            Contact us
                        </a>

                        {/* Mobile Menu Button */}
                        <button
                            className="md:hidden p-2"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                        >
                            {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>

                    {/* Mobile Navigation */}
                    {isMenuOpen && (
                        <div className="md:hidden border-t border-gray-200 pt-4 pb-3">
                            <div className="flex flex-col space-y-3">
                                <a href="#products" className="text-gray-700">Products</a>
                                <a href="#products" className="text-gray-700">About us</a>
                                <a href="#contact-us" className="text-gray-700">Contact</a>
                                <a href='#contact-us' className="bg-blue-600 text-white px-6 py-2 rounded-md text-sm font-medium w-fit">
                                    Contact us
                                </a>
                            </div>
                        </div>
                    )}
                </div>
            </header>

            {/* Hero Section */}
            {/* Stop Fraud Section */}
            <section className="py-12 lg:py-16 mt-20 bg-white">
                <div className="md:max-w-[85%] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                        <div className="max-w-xl">
                            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                                Stop Fraud Before It<br />
                                Happens. Not After.
                            </h1>
                            <p className="text-base lg:text-lg text-gray-600 mb-6 lg:mb-8 leading-relaxed">
                                Uncover hidden patterns, mitigate risks, and make informed decisions to stay ahead of evolving fraud tactics.
                            </p>
                            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg">
                                <input
                                    type="email"
                                    name="email"
                                    id="emailsass"
                                    value={email}
                                    onChange={handleChange}
                                    placeholder="Enter your email address"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                    required
                                    disabled={isSubmitting}
                                />
                                <button
                                    type="submit"
                                    disabled={isSubmitting || !email}
                                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                                >
                                    {isSubmitting ? 'Adding to waitlist...' : 'Join Waitlist'}
                                </button>
                            </form>
                        </div>

                        {/* Radar Chart Visualization */}
                        <div className="relative flex justify-center">
                            <img src='images/circle_indicator.png' className="w-full max-w-sm lg:max-w-full h-auto" alt="Circle Indicator" />
                        </div>
                    </div>

                    <div className="my-12 lg:my-20">
                        <p className="text-sm flex justify-center text-gray-500 mb-6 lg:mb-8 tracking-wide font-medium px-4">THE CUSTOMER DATA PLATFORM TRUSTED BY</p>
                        <div className="flex justify-center items-center space-x-8 lg:space-x-16 flex-wrap gap-4 lg:gap-6 px-4">
                            <span className="text-2xl font-bold text-green-600"><img src='images/credpal.svg' alt="Gamp" /></span>
                            <div className="flex items-center space-x-1">
                                <span className="text-xl font-bold text-blue-600"><img src='images/mkobo.svg' alt="CredPal" /></span>
                            </div>
                            <span className="text-xl font-bold text-gray-800"><img src='images/selar.svg' alt="Printivo" /></span>
                            <div className="flex items-center space-x-1">
                                <span className="text-xl font-bold text-gray-800"><img src='images/3mtt.svg' alt="Shipbubble" /></span>
                            </div>
                            <div className="flex items-center space-x-1">
                                <span className="text-xl font-bold text-gray-800"><img src='images/gigm.svg' alt="Pocket Food" /></span>
                            </div>
                            <div className="flex items-center space-x-1">
                                <span className="text-xl font-bold text-gray-800"><img src='images/fiberone.svg' alt="Pocket Food" /></span>
                            </div>
                            <div className="flex items-center space-x-1">
                                <span className="text-xl font-bold text-gray-800"><img src='images/middleman.svg' alt="Pocket Food" /></span>
                            </div>
                            <div className="flex items-center space-x-1">
                                <span className="text-xl font-bold text-gray-800"><img src='images/vale.svg' alt="Pocket Food" /></span>
                            </div>
                        </div>
                    </div>

                    <div id='products' className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 lg:mt-40">
                        <div className="text-center mb-12 lg:mb-16">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                                Predict. Prevent. Protect — Before<br />
                                Fraud Happens.
                            </h2>
                        </div>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                            <div className="rounded-lg p-4 lg:p-6" style={{ boxShadow: '0.5px 0.5px 1px 1.5px #E5E5E5' }}>
                                <img src='images/ai.png' className="w-12 h-12 lg:w-auto lg:h-auto" alt="AI Icon" />
                                <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-3 leading-tight mt-2">AI-Powered<br />Fraud Detection</h3>
                                <p className="text-gray-600 text-sm leading-relaxed">
                                    Our advanced machine learning algorithms analyze patterns and data to identify subtle anomalies and suspicious behaviors that humans review might miss.
                                </p>
                            </div>

                            <div className="rounded-lg p-4 lg:p-6" style={{ boxShadow: '0.5px 0.5px 1px 1.5px #E5E5E5' }}>
                                <img src='images/real_time.png' className="w-12 h-12 lg:w-auto lg:h-auto" alt="Real Time Icon" />
                                <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-3 leading-tight mt-2">Real-Time<br />Risk Scoring</h3>
                                <p className="text-gray-600 text-sm leading-relaxed">
                                    Assign risk scores to transactions and user activities based on a multitude of factors.
                                </p>
                            </div>

                            <div className="rounded-lg p-4 lg:p-6 sm:col-span-2 lg:col-span-1" style={{ boxShadow: '0.5px 0.5px 1px 1.5px #E5E5E5' }}>
                                <img src='images/behavioural.png' className="w-12 h-12 lg:w-auto lg:h-auto" alt="Behavioral Icon" />
                                <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-3 leading-tight mt-2">Behavioral<br />Analytics</h3>
                                <p className="text-gray-600 text-sm leading-relaxed">
                                    Understand legitimate user behavior and detect deviations that could indicate fraudulent activity.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Comprehensive Section */}
            <section className="py-12 mb-10 lg:py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                            Comprehensive Fraud<br />
                            Analytics and Reporting
                        </h2>
                        <p className="text-base lg:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                            Gain a holistic view of your fraud landscape with interactive dashboards and customizable reports.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-20 items-center">
                        <div className=" mx-auto md:p-6 bg-gray-50 rounded-2xl">
                            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
                                <img src='images/g1.svg' className="w-full h-auto p-4" alt="Customize" />
                                <div className="text-left">
                                    <h2 className="text-3xl font-bold text-gray-900 mb-4 leading-tight">
                                        Customizable<br />
                                        Rules Engine
                                    </h2>
                                    <p className="text-gray-600 text-lg leading-relaxed">
                                        Define and deploy your own fraud rules based on your unique risk appetite and business logic.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className=" mx-auto md:p-6 bg-gray-50 rounded-2xl">
                            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
                                <img src='images/g2.svg' className="w-full h-auto p-4" alt="Customize" />
                                <div className="text-left">
                                    <h2 className="text-3xl font-bold text-gray-900 mb-4 leading-tight">
                                        Seamless<br /> Integration
                                    </h2>
                                    <p className="text-gray-600 text-lg leading-relaxed">
                                        Easily integrate our platform with your existing systems and workflows through our robust APIs.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-12 lg:py-20 mb-6 lg:mb-10 bg-gradient-to-b from-[#3143B5] to-[#090B0E] rounded-2xl lg:rounded-3xl mx-4 lg:mx-8">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6 lg:mb-8 leading-tight">
                        Protect your business and your customers<br className="hidden sm:block" />
                        with our AI-powered fraud detection and<br className="hidden sm:block" />
                        analytics solution.
                    </h2>

                    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
                        <input
                            type="email"
                            name="email"
                            value={email}
                            onChange={handleChange}
                            placeholder="Enter your email address"
                            className="flex-1 px-4 py-3 rounded-md focus:outline-none  text-gray-900 text-sm"
                            required
                            disabled={isSubmitting}
                        />
                        <button
                            type="submit"
                            disabled={isSubmitting || !email}
                            className="bg-blue-600 text-white px-8 py-3 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                        >
                            {isSubmitting ? 'Adding to waitlist...' : 'Join Waitlist'}
                        </button>
                    </form>
                </div>
            </section>

            {/* Footer */}
            <footer id='contact-us' className="bg-gray-900 text-white py-12 lg:py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Main Footer Content */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12 lg:mb-16">
                        {/* Products Column */}
                        <div>
                            <h4 className="font-semibold mb-4 lg:mb-6 text-[#BDC6FF] text-base">Products</h4>
                            <ul className="space-y-3 lg:space-y-4 text-gray-400 text-sm">
                                <li><a href="#" className="hover:text-white transition-colors">AI-powered Customer Engagement</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Fraud Detection</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Monitor features</a></li>
                            </ul>
                        </div>

                        {/* Company Column */}
                        <div>
                            <h4 className="font-semibold mb-4 lg:mb-6 text-[#BDC6FF] text-base">Company</h4>
                            <ul className="space-y-3 lg:space-y-4 text-gray-400 text-sm">
                                <li><a href="#" className="hover:text-white transition-colors">About us</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Partnerships</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                            </ul>
                        </div>

                        {/* Contact Column */}
                        <div className="md:col-span-2">
                            <h4 className="font-semibold mb-4 lg:mb-6 text-[#BDC6FF] text-base">Contact</h4>
                            <div className="grid md:grid-cols-2 gap-6">
                                {/* Left Contact Info */}
                                <div className="space-y-4">
                                    <div className="flex items-start space-x-3">
                                        <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 mt-0.5">
                                            <img src='nigeria.svg' />
                                        </div>
                                        <div className="text-gray-400 text-sm">
                                            <p className='text-gray-400'>4, Ayanbayo Street,</p>
                                            <p className='text-gray-400'>off Faramobi Ajike Street,</p>
                                            <p className='text-gray-400'>Anthony, Lagos.</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start space-x-3">
                                        <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 mt-0.5">
                                            <img src='uk.svg' />
                                        </div>
                                        <div className="text-gray-400 text-sm">
                                            <p className='text-gray-400'>86-90, Paul Street, London</p>
                                            <p className='text-gray-400'>EC2A 4NE, England</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Contact Info */}
                                <div className="space-y-4">
                                    <div className="flex items-start space-x-3">
                                        <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 mt-0.5">
                                            <img src='us.svg' />
                                        </div>
                                        <div className="text-gray-400 text-sm">
                                            <p className='text-gray-400'>88522 11 NE 1st St, 8th</p>
                                            <p className='text-gray-400'>floor Miami-Dade, US</p>
                                            <p className='text-gray-400'>33132</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Section with Logo, Certifications, and Social */}
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 lg:gap-8">
                        {/* Left Side - Logo */}
                        <div className="flex items-center space-x-3">
                            <img src='white_logo.svg' className="lg:w-[200px] w-[150px] " alt="Bunce Logo" />
                        </div>

                        {/* Right Side - Social Media */}
                        <div className="flex items-center space-x-3 lg:space-x-4">
                            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer group">
                                <Instagram className="w-4 h-4 lg:w-5 lg:h-5 text-gray-400 group-hover:text-white" />
                            </div>
                            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer group">
                                <Twitter className="w-4 h-4 lg:w-5 lg:h-5 text-gray-400 group-hover:text-white" />
                            </div>
                            <a target='_blank' href={'https://www.linkedin.com/company/107639924/admin/dashboard/'}>
                                <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer group">
                                    <Linkedin className="w-4 h-4 lg:w-5 lg:h-5 text-gray-400 group-hover:text-white" />
                                </div>
                            </a>
                            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer group">
                                <Facebook className="w-4 h-4 lg:w-5 lg:h-5 text-gray-400 group-hover:text-white" />
                            </div>
                        </div>
                    </div>

                    {/* Copyright */}
                    <div className="border-gray-800 mt-8 lg:mt-12 pt-6 lg:pt-8">
                        <p className="text-gray-400 text-sm text-center lg:text-left">
                            © 2025 Bunce Technologies Ltd. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default BounceDataPlatformPage;